import { useReducer, useContext, useRef } from 'react';
import { workflowReducer, createInitialWorkflowState } from '../reducers/workflowReducer';
import { WorkflowState, Stage, IntakeData, SimilarRequestsResponse, PrioritizationAssessment } from '../types/workflow.types';
import { callIRISRich, getIRISErrorMessage } from '../services/IRIS';
import { createAsanaTask, AsanaTaskData, getEnumFieldConfig } from '../services/asanaService';
import { similarRequestsService } from '../services/similarRequestsService';
import { getOrganizationData } from '../services/organizationS3Service';
import { useLoadingState } from './useLoadingState';
import { 
  AGENT_IDS, 
  INITIAL_MESSAGES, 
  DATA_EXTRACTION_KEYWORDS
} from '../constants/fillyConstants';
import { DEFAULT_AGENT_CONFIG } from '../constants/defaultAgentConfig';
import { AgentConfig } from '../services/organizationService';
import { AuthenticatedUserContext } from '../auth';

/**
 * Custom hook that manages the entire intake workflow business logic
 * This extracts all the complex state management and API calls from the FillyOnIris component
 * 
 * Key responsibilities:
 * - Manages conversation state using useReducer
 * - Handles IRIS API communication
 * - Processes data extraction from conversations
 * - Manages Asana task creation
 * - Handles similar requests fetching
 * - Provides clean interface for UI components
 */
export const useIntakeWorkflow = () => {
  // Get authenticated user context
  const authenticatedUser = useContext(AuthenticatedUserContext);
  
  // Initialize state with user email
  const [state, dispatch] = useReducer(
    workflowReducer, 
    createInitialWorkflowState(authenticatedUser?.email || 'User')
  );
  
  // Use the existing loading state hook for compatibility
  const { setLoading: setLoadingState, clearLoading, isLoading: isLoadingState, getLoadingMessage } = useLoadingState();

  // Track last failed message for retry
  const lastFailedMessageRef = useRef<{ text: string; selectedPortfolio: string } | null>(null);

  /**
   * Adds a message to the conversation
   * Replaces the addMessage function from the original component
   */
  const addMessage = (role: "user" | "bot" | "system", content: string) => {
    console.log(`💬 ADD MESSAGE: Adding ${role} message to conversation`);
    
    switch (role) {
      case 'user':
        dispatch({ type: 'ADD_USER_MESSAGE', payload: content });
        break;
      case 'bot':
        dispatch({ type: 'ADD_BOT_MESSAGE', payload: content });
        break;
      case 'system':
        dispatch({ type: 'ADD_SYSTEM_MESSAGE', payload: content });
        break;
    }
    
    console.log(`📝 MESSAGE COUNT: Conversation now has ${state.messages.length + 1} messages`);
  };

  // Store resolved agent config — updated by the component via setAgentConfig
  const agentConfigRef = useRef<AgentConfig>(DEFAULT_AGENT_CONFIG);
  // Store resolved Asana PAT — updated by the component via setAsanaPat
  const asanaPatRef = useRef<string | undefined>(undefined);

  const setAgentConfig = (config: AgentConfig | undefined | null) => {
    if (config) {
      agentConfigRef.current = config;
    }
  };

  const setAsanaPat = (pat: string | undefined | null) => {
    asanaPatRef.current = pat || undefined;
  };

  /**
   * Communicates with IRIS API for different workflow stages
   * Uses resolved agent config from org/portfolio (with fallback to defaults)
   */
  const callLLM = async (userMessage: string, stageType: 'intake' | 'summarization' | 'finaljson'): Promise<string> => {
    console.log(`🔮 IRIS CALL: Starting ${stageType} stage processing`);
    
    try {
      const resolvedAgents = agentConfigRef.current;
      const agentId = stageType === 'finaljson' 
        ? (resolvedAgents.finaljson || AGENT_IDS.finaljson)
        : (resolvedAgents.chat || AGENT_IDS.intake);
      
      console.log(`🤖 AGENT: Using ${stageType === 'finaljson' ? 'finaljson' : 'chat'} agent: ${agentId}`);
      
      const irisRequest = {
        userMessage,
        stage: stageType,
        agentId,
        intakeData: state.intakeData,
        sessionId: state.sessionId
      };
      
      console.log(`🔗 IRIS SESSION: Using persistent session ID: ${state.sessionId}`);
      console.log(`📡 IRIS API CALL: Sending request to IRIS service for ${stageType} conversation`);
      
      const response = await callIRISRich(irisRequest);
      console.log(`✅ IRIS SUCCESS: Received response for ${stageType} stage`);
      return response;
    } catch (error) {
      console.error(`❌ IRIS ERROR: Failed ${stageType} stage processing:`, error);
      throw error;
    }
  };

  /**
   * Extracts structured data from conversation using keyword matching
   * This is the existing logic - will be improved in Phase 5
   * Replaces the updateIntakeData function from the original component
   */
  const updateIntakeData = (userInput: string, botResponse: string) => {
    console.log('📊 DATA EXTRACTION: Analyzing conversation for structured intake data');
    const lowerResponse = botResponse.toLowerCase();
    
    // Check bot response keywords to determine what type of data user just provided
    const extractedData: Partial<IntakeData> = {};
    
    Object.entries(DATA_EXTRACTION_KEYWORDS).forEach(([field, keywords]) => {
      if (!(state.intakeData as any)[field] && keywords.some(keyword => lowerResponse.includes(keyword))) {
        console.log(`🎯 ${field.toUpperCase()}: Extracted from user input`);
        extractedData[field as keyof IntakeData] = userInput;
      }
    });
    
    // Update state with extracted data
    if (Object.keys(extractedData).length > 0) {
      dispatch({ type: 'UPDATE_INTAKE_DATA', payload: extractedData });
    }
  };

  /**
   * Fetches similar requests from the backend
   * Replaces the fetchSimilarRequests function from the original component
   */
  const fetchSimilarRequests = async (
    problemStatement: string, 
    team: string, 
    program: string, 
    projectId: string
  ): Promise<SimilarRequestsResponse | null> => {
    console.log('🔍 SIMILAR REQUESTS: Starting fetch process');
    dispatch({ type: 'SET_SIMILAR_REQUESTS_LOADING', payload: true });
    
    // Add loading message to chat
    addMessage('system', '🔍 Loading related requests...');
    
    try {
      console.log('📡 SIMILAR REQUESTS API: Calling backend with filters:', { team, program, projectId });
      const result = await similarRequestsService.fetchSimilarRequests(
        problemStatement,
        team,
        program,
        projectId
      );
      
      dispatch({ type: 'SET_SIMILAR_REQUESTS', payload: result });
      console.log('✅ SIMILAR REQUESTS SUCCESS: Fetched and stored results:', {
        team,
        program,
        projectId,
        count: result.count,
        actual_requests: result.similar_requests?.length || 0
      });
      return result;
    } catch (error) {
      console.warn('⚠️ SIMILAR REQUESTS ERROR: Failed to fetch (non-critical):', error);
      const fallbackResult: SimilarRequestsResponse = { 
        count: 0, 
        similar_requests: [],
        query_info: {
          program_filter: program || ''
        }
      };
      dispatch({ type: 'SET_SIMILAR_REQUESTS', payload: fallbackResult });
      return null;
    } finally {
      dispatch({ type: 'SET_SIMILAR_REQUESTS_LOADING', payload: false });
      console.log('🏁 SIMILAR REQUESTS: Fetch process completed');
    }
  };

  /**
   * Generates summary and classification using IRIS
   * Replaces the generateSummaryAndClassification function from the original component
   */
  const generateSummaryAndClassification = async (selectedPortfolio: string) => {
    console.log('🎯 SUMMARY GENERATION: Starting final processing phase');
    setLoadingState('GENERATING_SUMMARY');
    
    try {
      console.log('📋 STEP 1: Generating AI classification and JSON structure');
      
      // Get themes and programs directly from Asana enum configuration
      let organizationContext = '';
      
      try {
        console.log('🔍 ASANA ENUM: Fetching live programs from Asana');
        const enumConfig = await getEnumFieldConfig(false, selectedPortfolio);
        
        // Extract only program names from Asana enum options
        const programs = Object.keys(enumConfig.program_name?.options || {}).filter(program => program.trim());
        
        console.log(`✅ ASANA ENUM SUCCESS: Found ${programs.length} programs`);
        
        if (programs.length > 0) {
          organizationContext = `

PROGRAM_LIST:
${programs.join('\n')}`;
        }
      } catch (error) {
        console.warn('⚠️ ASANA ENUM ERROR: Failed to fetch from Asana, falling back to organization data:', error);
        
        // Fallback to organization data if Asana enum fetch fails
        try {
          const orgData = await getOrganizationData();
          if (orgData && orgData.organizations.length > 0) {
            const programs = orgData.organizations
              .flatMap((org: any) => org.programs || [])
              .filter((program: string) => program.trim());
            
            console.log(`🔄 FALLBACK SUCCESS: Using ${programs.length} programs from organization data`);
            
            if (programs.length > 0) {
              organizationContext = `

PROGRAM_LIST:
${programs.join('\n')}`;
            }
          }
        } catch (fallbackError) {
          console.error('❌ FALLBACK ERROR: Both Asana and organization data failed:', fallbackError);
        }
      }
      
      const jsonResponse = await callLLM(`Generate JSON${organizationContext}`, 'finaljson');

      // Extract JSON from IRIS response format
      let jsonString = jsonResponse;
      
      if (typeof jsonResponse === 'object' && jsonResponse !== null && 'response' in jsonResponse) {
        jsonString = (jsonResponse as any).response;
      }
      
      let cleanedResponse = jsonString.trim();
      
      if (cleanedResponse.includes('```json')) {
        cleanedResponse = cleanedResponse
          .replace(/^[\s\S]*?```json\s*/, '')
          .replace(/\s*```[\s\S]*$/, '')
          .trim();
      }
      
      if (!cleanedResponse.startsWith('{')) {
        const jsonMatch = cleanedResponse.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          cleanedResponse = jsonMatch[0];
        }
      }
      
      console.log('🧹 JSON CLEANUP: Cleaned IRIS response for parsing', cleanedResponse);
      const parsed = JSON.parse(cleanedResponse);
      
      // Override target_year and request_type with pre-set values from request type selection
      const finalParsed = {
        ...parsed,
        target_year: state.intakeData.target_year || parsed.target_year,
        request_type: state.intakeData.request_type || parsed.request_type
      };
      
      console.log('📅 OVERRIDE VALUES: Using pre-set values from request type selection:', {
        target_year: finalParsed.target_year,
        request_type: finalParsed.request_type
      });
      
      dispatch({ type: 'SET_FINAL_JSON', payload: finalParsed });
      
      console.log("📦 AI CLASSIFICATION COMPLETE: Generated structured data:", {
        team: parsed.assigned_team,
        program: parsed.program_name,
        theme: parsed.theme
      });
      
      console.log('📋 STEP 2: Similar requests will be fetched by component with projectId');
      
      console.log('💬 SUMMARY DISPLAY: Showing classification to user');
      addMessage('system', `Perfect! I've gathered all the details about your request. Here's what I've captured:

**📋 Your Request Summary**
• **Title:** ${parsed.title}
• **Problem:** ${parsed.problem_statement}
• **Desired Outcome:** ${parsed.desired_outcome}
• **Business Impact:** ${parsed.business_impact}
• **Stakeholders:** ${parsed.stakeholders || 'Not specified'}
• **Program:** ${parsed.program_name || 'To be classified'}

Now let's confirm a few more details before submitting.`);
      
      console.log('🎯 STAGE TRANSITION: Moving to structured capture stage for user input');
      dispatch({ type: 'SET_STAGE', payload: Stage.StructuredCapture });
      
    } catch (error) {
      console.error("❌ SUMMARY GENERATION ERROR: Failed to process request:", error);
      const errorMessage = getIRISErrorMessage(error, 'Request processing');
      addMessage('system', `❌ ${errorMessage}`);
      dispatch({ type: 'SET_STAGE', payload: Stage.Intake });
    } finally {
      clearLoading();
      console.log('🏁 SUMMARY GENERATION: Process completed');
    }
  };

  /**
   * Submits the final request to Asana with enhanced error handling and retry capability
   * Replaces the submitToAsana function from the original component
   */
  const submitToAsana = async (selectedPortfolio: string, availableOrganizations: any[], retryAttempt: number = 0) => {
    console.log(`🚀 ASANA SUBMISSION: Starting submission process (attempt ${retryAttempt + 1})`);
    
    if (!state.finalJSON) {
      console.error('❌ ASANA ERROR: No finalJSON available for submission');
      addMessage('system', "❌ Error: No request data found. Please start over.");
      dispatch({ type: 'SET_STAGE', payload: Stage.Intake });
      return;
    }

    // selectedPortfolio IS the asanaProjectId (resolved by useOrganizations)
    // Orgs don't have Asana IDs — only portfolios do
    let projectId: string | undefined = selectedPortfolio || undefined;
    let selectedOrgName = 'Default Project';
    
    if (selectedPortfolio && availableOrganizations.length > 0) {
      // Find the portfolio name for display purposes
      for (const org of availableOrganizations) {
        const portfolios = (org as any).portfolios as any[] | undefined;
        if (portfolios) {
          const matchingPortfolio = portfolios.find((p: any) => p.asanaProjectId === selectedPortfolio);
          if (matchingPortfolio) {
            selectedOrgName = `${org.name} → ${matchingPortfolio.name}`;
            break;
          }
        }
        // Legacy fallback: org.asanaProject
        if (org.asanaProject === selectedPortfolio) {
          selectedOrgName = org.name;
          break;
        }
      }
      console.log(`🎯 PORTFOLIO SUBMISSION: Using ${selectedOrgName} (Project: ${projectId})`);
    }

    console.log('📋 ASANA PREPARATION: Preparing task data from finalJSON');
    dispatch({ type: 'SET_STAGE', payload: Stage.Complete });
    setLoadingState('ASANA_SUBMISSION');

    try {
      // Set created_at on the frontend in the user's local date (YYYY-MM-DD)
      const today = new Date();
      const createdAtDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      // Send ALL finalJSON fields to the backend (not just hardcoded ones)
      // This allows new custom fields from prompt config to flow through
      const asanaTaskData: any = {
        ...state.finalJSON,
        // Ensure required fields have values
        title: state.finalJSON.title,
        problem_statement: state.finalJSON.problem_statement || '',
        desired_outcome: state.finalJSON.desired_outcome || '',
        business_impact: state.finalJSON.business_impact || '',
        annualized_entitlement: state.finalJSON.annualized_entitlement || '0',
        impact_metrics: state.finalJSON.impact_metrics || state.finalJSON.entitlement_metrics || '',
        site_types: state.finalJSON.stakeholders || state.finalJSON.site_types || '',
        theme: state.finalJSON.theme || '',
        program_name: state.finalJSON.program_name || '',
        team: state.finalJSON.assigned_team || '',
        created_by: authenticatedUser?.email || 'User',
        created_at: createdAtDate,
        target_year: state.finalJSON.target_year || '',
        request_type: state.finalJSON.request_type || '',
        notes: state.finalJSON.conversation_summary || '',
      };

      // Always default op_decision to "New" for new submissions.
      // The prioritization assessment is informational only (shown in UI) — 
      // the actual OP decision is made by the PM during planning.
      asanaTaskData.op_decision = 'New';
      
      // Include prioritization rationale in notes if available (for PM context)
      if (state.prioritizationResult) {
        const rationaleText = `\n\n---\n📊 AI Priority Assessment (${new Date().toISOString().split('T')[0]}): ${state.prioritizationResult.meetsAcceptanceCriteria ? 'Meets Criteria' : 'Does Not Meet Criteria'} — ${state.prioritizationResult.rationale}`;
        asanaTaskData.notes = (asanaTaskData.notes || '') + rationaleText;
        console.log('📊 PRIORITIZATION DATA: op_decision set to "New", rationale appended to notes');
      }

      console.log('📋 ASANA TASK DATA: Prepared for submission');
      
      console.log('📡 ASANA API CALL: Creating task in Asana');
      const asanaResponse = await createAsanaTask(asanaTaskData, projectId, undefined, asanaPatRef.current);
      console.log('📨 ASANA RESPONSE: Received response');
      
      if (asanaResponse.success) {
        console.log('✅ ASANA SUCCESS: Task created successfully');
        dispatch({ type: 'COMPLETE_SUBMISSION', payload: { taskUrl: asanaResponse.taskUrl || '' } });
        
        addMessage('system', `✅ Feature request submitted successfully!

**Asana Task Created:**
- **Task:** ${asanaTaskData.title}
- **ID:** ${asanaResponse.taskId}
- **Portfolio:** ${selectedOrgName}
- **Link:** [View in Asana](${asanaResponse.taskUrl})

Your request has been assigned to **${state.finalJSON.assigned_team}** under the **${state.finalJSON.program_name}** program in the **${selectedOrgName}** portfolio.`);
      } else {
        throw new Error(asanaResponse.error || 'Asana task creation failed');
      }
    } catch (error) {
      console.error("❌ ASANA SUBMISSION ERROR: Failed to create task:", error);
      
      // Determine if error is retryable
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isRetryable = !errorMessage.toLowerCase().includes('authentication') && 
                         !errorMessage.toLowerCase().includes('permission') &&
                         !errorMessage.toLowerCase().includes('invalid') &&
                         retryAttempt < 2; // Max 3 attempts
      
      // Dispatch error to state for UI handling
      dispatch({ 
        type: 'SUBMISSION_ERROR', 
        payload: { 
          error: errorMessage, 
          retryable: isRetryable 
        } 
      });
      
      // Add user-friendly error message
      addMessage('system', `❌ Failed to create Asana task: ${errorMessage}

Your request details have been preserved:
- **Team:** ${state.finalJSON.assigned_team}
- **Program:** ${state.finalJSON.program_name}
- **Portfolio:** ${selectedOrgName}

${isRetryable ? '🔄 You can retry the submission or save your request details.' : '💾 Please save your request details and contact support.'}`);
      
    } finally {
      clearLoading();
      console.log('🏁 ASANA SUBMISSION: Process completed');
    }
  };

  /**
   * Handles user message input and processing
   * Replaces the handleUserMessage and handleIntakeMessage functions from the original component
   */
  const handleMessage = async (text: string, selectedPortfolio: string) => {
    if (!text.trim()) {
      console.log('⚠️ EMPTY MESSAGE: Ignoring empty user input');
      return;
    }

    console.log('📝 MESSAGE FLOW: Adding user message and starting processing');
    addMessage('user', text);
    setLoadingState('IRIS_PROCESSING');

    try {
      await handleIntakeMessage(text, selectedPortfolio);
      // Clear last failed message on success
      lastFailedMessageRef.current = null;
    } catch (error) {
      console.error("❌ MESSAGE HANDLER ERROR: Error processing user message:", error);
      // Store the failed message for retry
      lastFailedMessageRef.current = { text, selectedPortfolio };
      addMessage('system', `⚠️ Something went wrong processing your message. This can happen with complex requests — please try again.`);
    } finally {
      clearLoading();
      console.log('🏁 MESSAGE PROCESSING: User message processing completed');
    }
  };

  /**
   * Assesses if we have sufficient data to generate a summary
   * This provides fallback completion detection when IRIS signals are unclear
   */
  const assessDataCompleteness = (): boolean => {
    const { intakeData } = state;
    
    // Check for critical fields that indicate a complete request
    const hasProblemStatement = Boolean(intakeData.problem_statement && intakeData.problem_statement.length > 20);
    const hasDesiredOutcome = Boolean(intakeData.desired_outcome && intakeData.desired_outcome.length > 15);
    const hasBusinessImpact = Boolean(intakeData.business_impact && intakeData.business_impact.length > 15);
    
    // Check conversation depth - should have meaningful exchanges
    const userMessages = state.messages.filter(msg => msg.role === 'user').length;
    const botMessages = state.messages.filter(msg => msg.role === 'bot').length;
    const hasMinimumConversation = userMessages >= 3 && botMessages >= 3;
    
    console.log('📊 DATA COMPLETENESS ASSESSMENT:', {
      hasProblemStatement,
      hasDesiredOutcome,
      hasBusinessImpact,
      hasMinimumConversation,
      userMessages,
      botMessages
    });
    
    return hasProblemStatement && hasDesiredOutcome && hasBusinessImpact && hasMinimumConversation;
  };

  /**
   * Enhanced completion detection with multiple fallback layers
   */
  const checkForCompletion = async (botReply: string, selectedPortfolio: string): Promise<boolean> => {
    console.log('🔍 ENHANCED COMPLETION CHECK: Starting multi-layer detection');
    
    // Layer 1: Traditional IRIS signal detection
    const hasIRISSignal = botReply.includes("✅ All fields complete") || 
                         botReply.toLowerCase().includes("ready to summarize");
    
    if (hasIRISSignal) {
      console.log('✅ LAYER 1 SUCCESS: IRIS completion signal detected');
      await generateSummaryAndClassification(selectedPortfolio);
      return true;
    }
    
    // Layer 2: Data completeness analysis
    const isDataComplete = assessDataCompleteness();
    
    // Layer 3: Edit mode context - be more aggressive in edit mode
    const isInEditMode = Boolean(state.isEditMode);
    const editModeTime = state.editModeStartTime ? 
      (new Date().getTime() - state.editModeStartTime.getTime()) / 1000 : 0;
    
    // In edit mode, trigger completion after 2 exchanges or if data looks complete
    if (isInEditMode && (editModeTime > 30 || isDataComplete)) {
      console.log('✅ LAYER 3 SUCCESS: Edit mode completion triggered', {
        editModeTime,
        isDataComplete
      });
      
      dispatch({ type: 'EXIT_EDIT_MODE' });
      await generateSummaryAndClassification(selectedPortfolio);
      return true;
    }
    
    // Layer 4: Show manual option if data seems complete but no IRIS signal
    if (isDataComplete && !state.showManualSummaryOption) {
      console.log('🎯 LAYER 4: Showing manual summary option');
      dispatch({ type: 'SHOW_MANUAL_SUMMARY_OPTION', payload: true });
      
      addMessage('system', `🎯 **Ready to Review?**
      
I've gathered substantial information about your request. Would you like me to generate a summary for review?

You can continue adding details, or click "Generate Summary" when ready.`);
    }
    
    // Track completion attempt
    dispatch({ type: 'SET_LAST_COMPLETION_ATTEMPT' });
    
    return false;
  };

  /**
   * Manual summary generation triggered by user
   */
  const manualGenerateSummary = async (selectedPortfolio: string) => {
    console.log('👤 MANUAL SUMMARY: User triggered summary generation');
    
    dispatch({ type: 'SHOW_MANUAL_SUMMARY_OPTION', payload: false });
    dispatch({ type: 'EXIT_EDIT_MODE' });
    
    await generateSummaryAndClassification(selectedPortfolio);
  };

  /**
   * Processes messages during the intake stage
   * Enhanced with human-in-the-loop completion detection
   */
  const handleIntakeMessage = async (text: string, selectedPortfolio: string) => {
    console.log(`📝 INTAKE HANDLER: Processing message in ${state.stage} stage`);
    
    if (state.stage === Stage.Intake) {
      console.log('📋 INTAKE STAGE: Processing user input for data collection');
      
      const botReply = await callLLM(text, 'intake');
      
      // Check if the reply contains the completion signal
      const hasCompletionSignal = botReply.includes("✅ All fields complete") || 
                                   botReply.toLowerCase().includes("ready to summarize");
      
      if (hasCompletionSignal) {
        // Don't show the raw completion response (may contain JSON) — go straight to summary
        console.log('✅ COMPLETION SIGNAL DETECTED: Skipping raw display, generating summary');
        addMessage('system', '🎯 Great — I have everything I need! Generating your summary...');
        await generateSummaryAndClassification(selectedPortfolio);
      } else {
        // Normal response — display it and check for data completeness
        addMessage('bot', botReply);
        
        console.log('📊 DATA UPDATE: Extracting structured data from conversation');
        updateIntakeData(text, botReply);

        console.log('🔍 ENHANCED COMPLETION CHECK: Using multi-layer detection');
        // Only check layers 2-4 (data completeness, edit mode, manual option)
        const isDataComplete = assessDataCompleteness();
        const isInEditMode = Boolean(state.isEditMode);
        const editModeTime = state.editModeStartTime ? 
          (new Date().getTime() - state.editModeStartTime.getTime()) / 1000 : 0;
        
        if (isInEditMode && (editModeTime > 30 || isDataComplete)) {
          dispatch({ type: 'EXIT_EDIT_MODE' });
          await generateSummaryAndClassification(selectedPortfolio);
        } else if (isDataComplete && !state.showManualSummaryOption) {
          dispatch({ type: 'SHOW_MANUAL_SUMMARY_OPTION', payload: true });
          addMessage('system', `🎯 **Ready to Review?**\n\nI've gathered substantial information about your request. Would you like me to generate a summary for review?\n\nYou can continue adding details, or click "Generate Summary" when ready.`);
        }
        
        dispatch({ type: 'SET_LAST_COMPLETION_ATTEMPT' });
      }
    }
    else if (state.stage === Stage.Summarization) {
      console.log('📋 SUMMARIZATION STAGE: Processing user confirmation');
      const lowerText = text.toLowerCase();
      
      if (lowerText.includes('yes') || lowerText.includes('create') || 
          lowerText.includes('submit') || lowerText.includes('confirm')) {
        console.log('✅ USER CONFIRMED: User wants to submit request');
        // Note: submitToAsana will be called from the component with organization data
        return 'CONFIRMED';
      } else if (lowerText.includes('no') || lowerText.includes('cancel') || 
                 lowerText.includes('change') || lowerText.includes('edit')) {
        console.log('❌ USER DECLINED: User wants to make changes - entering edit mode');
        
        // Enter edit mode for enhanced tracking
        dispatch({ type: 'ENTER_EDIT_MODE' });
        
        // ENHANCED: Call IRIS to maintain conversation context when going back to edit mode
        try {
          const contextReply = await callLLM(text, 'intake');
          addMessage('bot', contextReply);
          
          // Update intake data based on the context-aware response
          updateIntakeData(text, contextReply);
          
          console.log('🔄 EDIT MODE ACTIVATED: IRIS context preserved during edit transition');
        } catch (error) {
          console.error('❌ CONTEXT ERROR: Failed to maintain IRIS context during edit transition:', error);
          // Fallback to generic message if IRIS call fails
          addMessage('bot', "No problem! Let's continue refining your request. What would you like to add or change?");
        }
        
        dispatch({ type: 'SET_STAGE', payload: Stage.Intake });
        return 'DECLINED';
      } else {
        console.log('❓ UNCLEAR RESPONSE: Asking for clarification');
        addMessage('bot', "Would you like to submit this feature request? Please say 'yes' to submit or 'no' to make changes.");
        return 'UNCLEAR';
      }
    }
    
    return 'PROCESSED';
  };

  /**
   * Handles request type selection (OP vs Urgent)
   * Replaces the handleRequestTypeSelection function from the original component
   */
  const handleRequestTypeSelection = (requestType: 'op' | 'urgent') => {
    dispatch({ type: 'SET_REQUEST_TYPE', payload: requestType });
    
    const currentYear = new Date().getFullYear();
    const nextYear = currentYear + 1;
    
    // Add confirmation message and proceed to intake
    const selectionMessage = requestType === 'op' 
      ? `📋 **OP Request for ${nextYear}** selected. This request will be planned for next year's operating plan.`
      : `🚨 **Urgent In-Year Request for ${currentYear}** selected. This request needs immediate attention for the current year.`;
    
    addMessage('system', selectionMessage);
    addMessage('bot', INITIAL_MESSAGES.intake);
  };

  /**
   * Resets the entire workflow to start over
   * Replaces the resetChat function from the original component
   */
  const resetChat = () => {
    console.log('🔄 CHAT RESET: User starting new conversation');
    
    dispatch({ type: 'RESET', payload: { userEmail: authenticatedUser?.email || 'User' } });
    similarRequestsService.clearCache();
    
    console.log('🔄 RESET COMPLETE: New session started');
  };

  /**
   * Toggles the similar requests sidebar
   */
  const toggleSidebar = () => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  };

  /**
   * Retries Asana submission after an error
   */
  const retryAsanaSubmission = (selectedPortfolio: string, availableOrganizations: any[]) => {
    console.log('🔄 RETRY ASANA: User initiated retry for Asana submission');
    
    // Clear previous error
    dispatch({ type: 'SUBMISSION_ERROR', payload: { error: '', retryable: false } });
    
    // Get current retry attempt from error context or default to 0
    const currentAttempt = state.submissionError?.context?.retryAttempt || 0;
    
    // Retry submission with incremented attempt count
    submitToAsana(selectedPortfolio, availableOrganizations, currentAttempt + 1);
  };

  /**
   * Exports request data in multiple formats for manual saving when Asana fails
   */
  const exportRequestData = (format: 'json' | 'csv' | 'txt' | 'email' = 'json') => {
    if (!state.finalJSON) {
      console.warn('⚠️ EXPORT: No request data available to export');
      return null;
    }

    const baseData = {
      timestamp: new Date().toISOString(),
      sessionId: state.sessionId,
      requestData: state.finalJSON,
      conversationSummary: state.messages
        .filter(msg => msg.role !== 'system')
        .map(msg => `${msg.role.toUpperCase()}: ${msg.content}`)
        .join('\n\n'),
      submissionError: state.submissionError?.message || 'Unknown error'
    };

    console.log(`📄 EXPORT: Request data prepared for ${format.toUpperCase()} download`);

    switch (format) {
      case 'json':
        return {
          data: JSON.stringify(baseData, null, 2),
          filename: `filly-request-${baseData.sessionId}.json`,
          mimeType: 'application/json'
        };

      case 'csv':
        const csvData = [
          ['Field', 'Value'],
          ['Title', state.finalJSON.title || ''],
          ['Problem Statement', state.finalJSON.problem_statement || ''],
          ['Desired Outcome', state.finalJSON.desired_outcome || ''],
          ['Business Impact', state.finalJSON.business_impact || ''],
          ['Impact Metrics', state.finalJSON.impact_metrics || ''],
          ['Urgency', state.finalJSON.urgency || ''],
          ['Assigned Team', state.finalJSON.assigned_team || ''],
          ['Program', state.finalJSON.program_name || ''],
          ['Theme', state.finalJSON.theme || ''],
          ['Target Year', state.finalJSON.target_year || ''],
          ['Request Type', state.finalJSON.request_type || ''],
          ['Submitted By', state.finalJSON.created_by || authenticatedUser?.email || ''],
          ['Date Submitted', new Date().toLocaleDateString()],
          ['Session ID', baseData.sessionId],
          ['Error', baseData.submissionError]
        ].map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');

        return {
          data: csvData,
          filename: `filly-request-${baseData.sessionId}.csv`,
          mimeType: 'text/csv'
        };

      case 'txt':
        const txtData = `FILLY FEATURE REQUEST - EXPORT

SUBMISSION DETAILS:
- Date: ${new Date().toLocaleString()}
- Session ID: ${baseData.sessionId}
- Submitted By: ${authenticatedUser?.email || 'User'}
- Error: ${baseData.submissionError}

REQUEST INFORMATION:
- Title: ${state.finalJSON.title || 'N/A'}
- Problem Statement: ${state.finalJSON.problem_statement || 'N/A'}
- Desired Outcome: ${state.finalJSON.desired_outcome || 'N/A'}
- Business Impact: ${state.finalJSON.business_impact || 'N/A'}
- Impact Metrics: ${state.finalJSON.impact_metrics || 'N/A'}
- Urgency: ${state.finalJSON.urgency || 'N/A'}

ROUTING INFORMATION:
- Assigned Team: ${state.finalJSON.assigned_team || 'N/A'}
- Program: ${state.finalJSON.program_name || 'N/A'}
- Theme: ${state.finalJSON.theme || 'N/A'}
- Target Year: ${state.finalJSON.target_year || 'N/A'}
- Request Type: ${state.finalJSON.request_type || 'N/A'}

CONVERSATION SUMMARY:
${baseData.conversationSummary}

Generated by Filly on Iris System`;

        return {
          data: txtData,
          filename: `filly-request-${baseData.sessionId}.txt`,
          mimeType: 'text/plain'
        };

      case 'email':
        const emailSubject = `Feature Request: ${state.finalJSON.title || 'Untitled Request'}`;
        const emailBody = `Hi Team,

I submitted a feature request through Filly, but there was an issue creating the Asana task. Here are the details:

PROBLEM STATEMENT:
${state.finalJSON.problem_statement || 'N/A'}

DESIRED OUTCOME:
${state.finalJSON.desired_outcome || 'N/A'}

BUSINESS IMPACT:
${state.finalJSON.business_impact || 'N/A'}

ROUTING INFORMATION:
- Team: ${state.finalJSON.assigned_team || 'N/A'}
- Program: ${state.finalJSON.program_name || 'N/A'}
- Theme: ${state.finalJSON.theme || 'N/A'}
- Target Year: ${state.finalJSON.target_year || 'N/A'}
- Request Type: ${state.finalJSON.request_type || 'N/A'}

ERROR DETAILS:
${baseData.submissionError}

SESSION ID: ${baseData.sessionId}

Please help create this task manually or investigate the submission issue.

Thanks,
${authenticatedUser?.email || 'User'}`;

        return {
          data: `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`,
          filename: 'email-draft',
          mimeType: 'text/email'
        };

      default:
        return {
          data: JSON.stringify(baseData, null, 2),
          filename: `filly-request-${baseData.sessionId}.json`,
          mimeType: 'application/json'
        };
    }
  };

  /**
   * Downloads the exported data
   */
  const downloadExportedData = (format: 'json' | 'csv' | 'txt' | 'email' = 'json') => {
    const exportResult = exportRequestData(format);
    if (!exportResult) return;

    if (format === 'email') {
      // Open email client
      window.location.href = exportResult.data;
      return;
    }

    // Download file
    const blob = new Blob([exportResult.data], { type: exportResult.mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportResult.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log(`📥 DOWNLOAD: ${format.toUpperCase()} file downloaded: ${exportResult.filename}`);
  };

  /**
   * Copies request data to clipboard
   */
  const copyToClipboard = async (format: 'json' | 'txt' = 'txt') => {
    const exportResult = exportRequestData(format);
    if (!exportResult) return false;

    try {
      await navigator.clipboard.writeText(exportResult.data);
      console.log(`📋 CLIPBOARD: ${format.toUpperCase()} data copied to clipboard`);
      return true;
    } catch (error) {
      console.error('❌ CLIPBOARD ERROR: Failed to copy to clipboard:', error);
      return false;
    }
  };

  /**
   * Clears submission error state
   */
  const clearSubmissionError = () => {
    dispatch({ type: 'SUBMISSION_ERROR', payload: { error: '', retryable: false } });
  };

  /**
   * Runs the AI prioritization check against the finalized request data.
   * Called automatically when finalJSON is set (during StructuredCapture stage).
   * Uses the existing prioritize agent to assess whether the request meets acceptance criteria.
   * Result is shown in the Summarization stage before submit.
   * 
   * @param finalJSONOverride - Optional: pass finalJSON directly to avoid stale closure issues
   */
  const runPrioritizationCheck = async (finalJSONOverride?: any) => {
    const jsonData = finalJSONOverride || state.finalJSON;
    if (!jsonData) {
      console.warn('⚠️ PRIORITIZATION: No finalJSON available for assessment');
      return;
    }

    console.log('📊 PRIORITIZATION: Starting pre-submit priority assessment');
    dispatch({ type: 'SET_PRIORITIZATION_LOADING', payload: true });

    try {
      const resolvedAgents = agentConfigRef.current;
      const prioritizeAgentId = resolvedAgents.prioritize || DEFAULT_AGENT_CONFIG.prioritize || '';

      if (!prioritizeAgentId) {
        console.warn('⚠️ PRIORITIZATION: No prioritize agent configured, skipping');
        dispatch({ type: 'SET_PRIORITIZATION_LOADING', payload: false });
        return;
      }

      // Build a single-task assessment prompt
      const taskForAssessment = {
        name: jsonData.title || '',
        problem_statement: jsonData.problem_statement || '',
        desired_outcome: jsonData.desired_outcome || '',
        business_impact: jsonData.business_impact || '',
        annualized_entitlement: jsonData.annualized_entitlement || '0',
        request_type: jsonData.request_type || '',
        target_year: jsonData.target_year || '',
      };

      const userMessage = `Assess this single request and determine if it meets acceptance criteria for prioritization. Return a JSON object with these fields:
- op_decision: one of "P1", "P2", "ATL", "BTL"
- mission_fit: one of "High", "Medium", "Low"
- meets_criteria: true or false (true if P1 or P2, false if ATL or BTL)
- rationale: a 1-2 sentence explanation of why this request does or does not meet acceptance criteria

Request to assess:
${JSON.stringify(taskForAssessment, null, 2)}`;

      console.log('📡 PRIORITIZATION: Calling IRIS prioritize agent');
      const response = await callIRISRich({
        userMessage,
        stage: 'finaljson',
        agentId: prioritizeAgentId,
        sessionId: `prioritize_${state.sessionId}`
      });

      // Parse the response
      let parsedResult: any;
      let responseText = response;

      // Clean up response (may be wrapped in markdown code blocks)
      if (responseText.includes('```json')) {
        responseText = responseText.replace(/^[\s\S]*?```json\s*/, '').replace(/\s*```[\s\S]*$/, '').trim();
      }
      if (!responseText.startsWith('{')) {
        const jsonMatch = responseText.match(/\{[\s\S]*\}/);
        if (jsonMatch) responseText = jsonMatch[0];
      }

      try {
        parsedResult = JSON.parse(responseText);
      } catch (parseErr) {
        console.warn('⚠️ PRIORITIZATION: Failed to parse JSON response, attempting extraction');
        // Fallback: try to extract key fields from text
        const meetsMatch = responseText.toLowerCase().includes('p1') || responseText.toLowerCase().includes('p2');
        parsedResult = {
          op_decision: meetsMatch ? 'P2' : 'BTL',
          mission_fit: 'Medium',
          meets_criteria: meetsMatch,
          rationale: responseText.substring(0, 200)
        };
      }

      // Determine if criteria is met (P1 or P2 = meets, ATL/BTL = does not)
      const opDecision = (parsedResult.op_decision || '').toUpperCase();
      const meetsCriteria = parsedResult.meets_criteria === true || 
                           opDecision === 'P1' || opDecision === 'P2';

      const assessment: PrioritizationAssessment = {
        meetsAcceptanceCriteria: meetsCriteria,
        op_decision: opDecision || 'TBD',
        mission_fit: parsedResult.mission_fit || 'Medium',
        rationale: parsedResult.rationale || 'Assessment completed.',
      };

      console.log('✅ PRIORITIZATION COMPLETE:', assessment);
      dispatch({ type: 'SET_PRIORITIZATION_RESULT', payload: assessment });

    } catch (error) {
      console.error('❌ PRIORITIZATION ERROR: Failed to run priority check:', error);
      // Non-critical — just stop loading, don't show error to user
      dispatch({ type: 'SET_PRIORITIZATION_LOADING', payload: false });
    }
  };

  /**
   * Dismisses the prioritization signal card
   */
  const dismissPrioritization = () => {
    dispatch({ type: 'DISMISS_PRIORITIZATION' });
  };

    // Return the state and all the action functions  
    // This provides a clean interface for the UI component
    return {
      // Config setters — call from component when resolvedConfig changes
      setAgentConfig,
      setAsanaPat,
    // State
    ...state,
    isLoadingState,
    getLoadingMessage,
    
    // Actions
    handleMessage,
    handleRequestTypeSelection,
    submitToAsana,
    retryAsanaSubmission,
    exportRequestData,
    clearSubmissionError,
    generateSummaryAndClassification,
    manualGenerateSummary,
    resetChat,
    toggleSidebar,
    addMessage,
    fetchSimilarRequests,
    
    // Prioritization
    runPrioritizationCheck,
    dismissPrioritization,
    
    // Retry last failed message
    retryLastMessage: () => {
      if (lastFailedMessageRef.current) {
        const { text, selectedPortfolio } = lastFailedMessageRef.current;
        handleMessage(text, selectedPortfolio);
      }
    },
    hasFailedMessage: lastFailedMessageRef.current !== null,
    
    // Direct dispatch access for simple state updates
    dispatch
  };
};
